import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/trailer-livestock-black.jpg";
import heroPicture from "@/assets/trailer-livestock-black.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import shareImage from "@/assets/trailer-livestock-black.jpg";
import gooseneck from "@/assets/trailer-red-gooseneck-installed.jpg";
import goosenectShop from "@/assets/trailer-red-gooseneck.jpg";
import tanVinylGooseneck from "@/assets/stock-trailer-tan-vinyl-top-gooseneck.jpg";
import whiteVinylFairwest from "@/assets/stock-trailer-white-vinyl-top-fairwest.jpg";
import ranchTractorSunset from "@/assets/ranch-tractor-sunset.jpg";
import grayLacingCorner from "@/assets/stock-trailer-gray-lacing-corner.jpg";
import grayGooseneckTop from "@/assets/stock-trailer-gooseneck-gray-top.jpg";
import tanLongGooseneck from "@/assets/stock-trailer-tan-long-gooseneck.jpg";
import gooseneckFrontTarp from "@/assets/stock-trailer-gooseneck-front-tarp.jpg";
import blueStockTanTarp from "@/assets/blue-stock-trailer-tan-tarp.jpg";
import tanRanchTrailer from "@/assets/tan-stock-trailer-cover-texas-ranch.jpg";
import { StockTrailerForm } from "@/components/StockTrailerForm";

const featuredTanRanch = (
  <section className="border-t-4 border-navy-deep bg-cream py-16">
    <div className="mx-auto max-w-7xl px-4 lg:px-8">
      <figure className="sm:float-left sm:mr-6 sm:mb-3 sm:w-[48%] md:w-[44%] lg:w-[40%]">
        <img
          src={tanRanchTrailer}
          alt="Custom tan heavy-duty vinyl stock trailer cover by Texas Made Shade installed on a gooseneck livestock trailer parked on a Texas Hill Country ranch"
          width={1200}
          height={900}
          className="block h-auto w-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <figcaption className="mt-2 text-xs uppercase tracking-[0.2em] text-navy-deep/70">
          Tan vinyl stock trailer top · Texas Hill Country ranch
        </figcaption>
      </figure>
      <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Featured Build</div>
      <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
        Tan Vinyl Stock Trailer Top — Patterned for the Ranch
      </h2>
      <p className="mt-4 text-lg text-navy-deep/80">
        This long-bed gooseneck stock trailer was patterned in our Seguin shop and fitted with a custom tan
        heavy-duty vinyl-coated polyester top. Tan reads soft against Texas dust and reflects sun off the cattle
        below — a working-ranch favorite for hauling between pasture, sale barn and lease.
      </p>
      <p className="mt-4 text-lg text-navy-deep/80">
        Reinforced edges, brass grommets and shock-cord lacing tie the top tight to the rail so it holds steady at
        highway speed. Every cover is sewn to the exact frame of your trailer — W-W, Calico, Featherlite, GR or
        custom — with hardware that handles daily ranch use across South and Central Texas.
      </p>
      <div className="clear-both" />
    </div>
  </section>
);

export const Route = createFileRoute("/stock-trailer-covers")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Stock Trailer Tarps & Covers — Texas | Texas Made Shade",
      description: "Custom-built stock trailer covers and tarp tops for livestock, gooseneck, and bumper-pull trailers. Heavy-duty vinyl and canvas, patterned to your trailer.",
      image: shareImage,
      imageAlt: "Custom black livestock stock trailer cover by Texas Made Shade",
      socialTitle: "Stock Trailer Covers — Texas Made Shade",
      socialDescription: "Custom livestock and gooseneck trailer covers built in Texas.",
      keywords: "Stock Trailer Covers Texas, stock trailer covers Texas, cattle trailer covers, livestock trailer tops Texas, gooseneck trailer covers Texas, custom trailer tarps Texas, stock trailer tops Seguin, ranch trailer covers Texas, W-W trailer covers, Calico trailer covers, Featherlite trailer covers",
    }),
    scripts: serviceHeadScripts({
      path: "/stock-trailer-covers",
      serviceName: "Stock Trailer Covers",
      breadcrumbName: "Stock Trailer Covers",
      description: "Custom canvas and vinyl tops, lacing and roll-up curtains for gooseneck and bumper-pull stock trailers across Texas.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/stock-trailer-covers"
      serviceName="Stock Trailer Covers"
      eyebrow="★ STOCK TRAILER COVERS ★"
      title="Stock Trailer Covers — Livestock, Gooseneck & Bumper-Pull"
      intro="Custom stock trailer covers built for working Texas ranchers. Heavy-duty vinyl-coated polyester and marine canvas, patterned to your exact trailer, hardware-fastened to handle highway speeds and Texas weather."
      heroImage={heroPicture}
      hideLetsTalkForm
      preContent={<><StockTrailerForm />{featuredTanRanch}</>}
      heroCta={{
        eyebrow: "Free Written Estimate",
        headline: "Request a Stock Trailer Cover Quote",
        subhead: "Tell us your trailer size and brand — we'll send a written estimate. Texas only.",
        primaryLabel: "Request a Quote",
        primaryHref: "#stock-trailer-quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Patterned to your exact trailer — any brand, any size",
        "Heavy-duty vinyl-coated polyester or marine canvas",
        "Reinforced grommets, hardware-fastened to the trailer",
        "Built to handle highway speeds and crosswinds",
        "Livestock, gooseneck, bumper-pull and flatbed trailers",
        "Repair and replacement service for worn-out tops",
      ]}
      body={[
        { heading: "Livestock Stock Trailer Tops", text: "Shade and rain protection for cattle, horses, sheep and goats during transport. Built tough enough to handle daily ranch use and pasture-to-sale-barn miles." },
        { heading: "Gooseneck & Bumper-Pull Covers", text: "Full-length gooseneck tops, bumper-pull tarps, and partial covers — patterned for any trailer brand including W-W, Calico, Featherlite, GR, and more." },
        { heading: "Flatbed & Equipment Tarps", text: "Custom tarps and covers for flatbeds, equipment trailers, and hauler trailers — built to your specs for fit and tie-down." },
        { heading: "Repair & Replacement", text: "Old top torn or thread-rotted? We replace, re-stitch, and re-grommet existing trailer covers in our Seguin shop." },
      ]}
      applications={["Livestock Trailers", "Goosenecks", "Bumper-Pull Stock", "Horse Trailers", "Flatbeds", "Equipment Haulers"]}
      faqs={[
        { q: "Will the cover fit my trailer brand?", a: "Yes. We pattern every cover to your specific trailer — W-W, Calico, Featherlite, GR, Titan, or any custom build. Bring it to our Seguin shop or we'll come measure." },
        { q: "What material do you use for stock trailer tops?", a: "Heavy-duty vinyl-coated polyester for most working livestock applications, and marine canvas for lighter-duty or premium-look applications. We'll recommend based on your use." },
        { q: "How long does a stock trailer cover last?", a: "A properly-built vinyl top typically lasts 6–10 years of daily ranch use in Texas weather, longer with lighter use." },
        { q: "Do you ship outside the Seguin area?", a: "Most stock trailer covers are pattern-fit, so we recommend bringing the trailer to our Seguin shop — but we install across South & Central Texas. Call +1 (830) 390-9588 to discuss." },
      ]}
      gallerySection={{
        eyebrow: "Stock Trailer Work",
        title: "Recent Trailer Cover Builds",
        images: [
          { src: hero, alt: "Black livestock stock trailer cover" },
          { src: gooseneck, alt: "Red gooseneck trailer cover installed" },
          { src: goosenectShop, alt: "Red gooseneck trailer cover in the Seguin shop" },
          { src: tanVinylGooseneck, alt: "Tan heavy-duty vinyl top cover patterned and installed on a vintage rust-patina gooseneck cattle trailer in Seguin, TX" },
          { src: whiteVinylFairwest, alt: "White 18oz vinyl top tarp custom-fit to a Fair-West gooseneck stock trailer with spare-tire mount, built by Texas Made Shade in Seguin" },
          { src: ranchTractorSunset, alt: "Massey Ferguson tractor cutting hay at sunset behind a Texas ranch gate — the kind of working operation Texas Made Shade builds stock trailer covers and tractor tops for" },
          { src: grayGooseneckTop, alt: "Long red gooseneck stock trailer fitted with a custom gray vinyl top tarp built and installed by Texas Made Shade in Seguin, TX" },
          { src: tanLongGooseneck, alt: "Brown long-bed gooseneck cattle trailer with two spare tires, fitted with a tan heavy-duty vinyl top cover patterned by Texas Made Shade" },
          { src: gooseneckFrontTarp, alt: "Close-up of the front gooseneck corner on a red stock trailer showing a custom gray vinyl top laced down through brass grommets with shock cord" },
          { src: grayLacingCorner, alt: "Detail of the rear corner of a red gooseneck stock trailer cover showing reinforced gray vinyl edge, brass grommets, and black shock-cord lacing tied to the top rail" },
          { src: blueStockTanTarp, alt: "Bright blue bumper-pull livestock stock trailer fitted with a custom tan heavy-duty vinyl top tarp patterned, sewn and installed by Texas Made Shade in Seguin, Texas" },
        ],
      }}
    >
      <section className="border-t-4 border-navy-deep bg-cream py-16">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <div className="text-center">
            <div className="font-display text-sm tracking-[0.3em] text-red">★ WATCH</div>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
              Stock Trailer Cover — In Action
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground">
              A quick look at one of our custom-fit stock trailer covers built and installed
              from our Seguin, TX shop.
            </p>
          </div>
          <div className="mx-auto mt-8 grid w-full max-w-5xl gap-6 md:grid-cols-2">
            <div className="border-4 border-navy-deep bg-black shadow-card">
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <iframe
                  title="Texas Made Shade stock trailer cover video"
                  src="https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Fshare%2Fv%2F18kSBuq4YF%2F&show_text=false&autoplay=false"
                  className="absolute inset-0 h-full w-full"
                  style={{ border: "none", overflow: "hidden" }}
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="border-4 border-navy-deep bg-black shadow-card">
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <iframe
                  title="Texas Made Shade stock trailer cover — second video"
                  src="https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Fshare%2Fv%2F1bJvxtxNkf%2F&show_text=false&autoplay=false"
                  className="absolute inset-0 h-full w-full"
                  style={{ border: "none", overflow: "hidden" }}
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </ServicePage>
  ),
});
